import React, { useState } from 'react';
import { Divider, Grid, Stack } from '@mui/material';
import { useToast } from '@shared/hooks/useToast';
import { useGenericDrawer } from '@shared/shell/drawer/useGenericDrawer';
import { useBottomSheet, BottomSheetSize } from '@shared/shell/bottomsheet/useBottomSheet';
import { PurchaseOrderDetails } from '@features/purchasing';
import { useProductInventory } from '@features/inventory/hooks/useInventory';
import { usePurchaseOrderItems } from '@features/purchasing/hooks/usePurchaseOrderItems';
import { useProduct } from '@features/index';
import { PurchaseOrder } from '@domain/purchasing';
import { CreatePurchaseOrderFlow } from '@features/purchasing/components/create/CreatePurchaseOrderFlow';
import { useNavigate, useParams } from 'react-router-dom';
import { Product } from '@domain/product/models/Product';
import AddProductPartBottomSheet from '../parts/AddProductParts';
import AddProductToUsedInBottomSheet from '../used-in/AddProductToUsedInBottomSheet';
import { container } from '@infrastructure/container';
import { useQueryClient } from '@tanstack/react-query';

import {
  ProductInventoryPreview,
  ProductDetailsSkeleton,
  ProductEmptyState,
  ProductInfoHeader,
  ProductPurchaseOrderList,
  ProductPartsList,
  ProductUsedInList,
  ProductFields,
  ProductOrderingSettings,
  ProductSuppliersBlock,
} from '.';
import ProductListings from './ProductListings';
import ProductCollections from './ProductCollections';
import CreateCollectionBottomSheet from '@features/collections/components/CreateCollectionBottomSheet';
import LinkProductToCollectionsSheet from '@features/collections/components/LinkProductToCollectionsSheet';
import { ProductSelectBottomSheet } from '@features/index';

const ProductInformation: React.FC = () => {
  const { productId: productIdParam } = useParams<{ productId: string }>();
  const productId = productIdParam;

  const toast = useToast();
  const [showFullDescription, setShowFullDescription] = useState(false);
  const { closeDrawer, openDrawer } = useGenericDrawer();
  const { openBottomSheet, closeBottomSheet } = useBottomSheet();
  const [showAllPurchaseOrderItems, setShowAllPurchaseOrderItems] = useState(false);

  const navigate = useNavigate();

  if (!productId) {
    navigate('/product-catalog/products');
    return null;
  }

  const { inventory } = useProductInventory(productId);
  const { product, isLoading } = useProduct(productId);
  const queryClient = useQueryClient();

  const handlePurchaseOrderItemClick = (purchaseOrder: PurchaseOrder) => {
    if (purchaseOrder.id) {
      openDrawer(
        'Purchase Order Details',
        <PurchaseOrderDetails purchaseOrderId={purchaseOrder.id} />,
        closeDrawer
      );
    }
  };

  const handleAddParts = async (selections: { product: { id: string }; quantity: number }[]) => {
    if (!product?.id) return;

    await container.productRepository.addProductParts(product.id, selections.map(({ product, quantity }) => ({
      productId: product.id,
      quantity,
    })));

    queryClient.invalidateQueries({ queryKey: ['product', product.id] });
    queryClient.invalidateQueries({ queryKey: ['products'] });
    toast.success('Parts added');
    closeBottomSheet();
  };

  const handleOpenAddParts = () => {
    openBottomSheet({
      title: 'Add Parts',
      component: (
        <AddProductPartBottomSheet
          productId={product?.id}
          existingPartProductIds={new Set(product?.parts?.map((p) => p.part.id) ?? [])}
          onAdd={handleAddParts}
          onClose={closeBottomSheet}
        />
      ),
      size: BottomSheetSize.MD,
    });
  };

  const handleAddUsedIn = async (selections: { product: Product; quantity: number }[]) => {
    if (!product?.id) return;
    for (const { product: parentProduct, quantity } of selections) {
      const result = await container.productRepository.addProductParts(parentProduct.id, [
        { productId: product.id, quantity },
      ]);
      if (result.status === 'error') throw new Error(result.error.message);
    }
    queryClient.invalidateQueries({ queryKey: ['product', product.id] });
    queryClient.invalidateQueries({ queryKey: ['products'] });
    toast.success('Added to products');
    closeBottomSheet();
  };

  const handleOpenAddUsedIn = () => {
    openBottomSheet({
      title: 'Add to Product',
      component: (
        <AddProductToUsedInBottomSheet
          currentProductId={product?.id ?? ''}
          existingUsedInProductIds={new Set(product?.usedIn?.map((u) => u.usedIn.id) ?? [])}
          onAdd={handleAddUsedIn}
          onClose={closeBottomSheet}
        />
      ),
      size: BottomSheetSize.MD,
    });
  };

  return (

    <Stack gap={8} sx={{ flexGrow: 1, py: 0 }}>
      {isLoading ? (
        <ProductDetailsSkeleton />
      ) : !product ? (
        <ProductEmptyState />
      ) : (
        <Grid container spacing={12} sx={{ flexGrow: 1, pt: 2 }}>
          <Grid item xs={12} md={8}>
            <Stack gap={6}>

              <ProductOrderingSettings product={product} />

              <ProductInventoryPreview inventory={inventory} />

              <ProductPurchaseOrderList
                product={product}
                onItemClick={handlePurchaseOrderItemClick}
              />

              <ProductSuppliersBlock product={product} />

              <ProductCollections
                collections={product.collections ?? []}
                product={product}
              />

              <ProductPartsList
                productId={product.id}
                parts={product.parts}
                onAddPart={handleOpenAddParts}
              />
              <ProductUsedInList
                productId={product.id}
                usedIn={product.usedIn}
                onAddUsedIn={handleOpenAddUsedIn}
              />

              <ProductListings
                listings={product.listings ?? []}
              />
            </Stack>
          </Grid>


          <Grid item xs={12} md={4}>
            <Stack gap={6} position="sticky" top={80} zIndex={1000}>
              <ProductInfoHeader
                product={product}
                showFullDescription={showFullDescription}
                onToggleDescription={() => setShowFullDescription(!showFullDescription)}
              />


              <Divider />

              <ProductFields
                product={product}
              />

            </Stack>
          </Grid>


        </Grid>
      )}
    </Stack>

  );
};

export default ProductInformation;
